import React, { useState, useEffect } from "react";
import EditDialogStyle from "styles/application/EditDialogStyle";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import InputAdornment from "@mui/material/InputAdornment";
import * as yup from "yup";
import { handleValidateObj, generateErrorState } from "helpers/utils";
import EMICheckbox from "components/Elements/EMICheckbox";
import { THRESHOLD_MODE } from "helpers/conditionMonitoringAlert";
import {
	getConditionMonitoringAlert,
	saveConditionMonitoringAlert,
} from "services/analytics";

const AED = EditDialogStyle();

const schema = (alertEnabled) =>
	yup.object({
		enableConditionMonitoringAlert: yup.bool(),
		alertThresholdMode: yup.string().nullable(),
		alertAboveThreshold: yup
			.number()
			.nullable()
			.typeError("Above Threshold must be a number"),
		alertBelowThreshold: yup
			.number()
			.nullable()
			.typeError("Below Threshold must be a number"),
		alertMessage: alertEnabled
			? yup.string().trim().required("Alert Message is required")
			: yup.string().nullable(),
	});

const defaultState = {
	// Opening the dialog implies intent to configure an alert, so default it on
	// and show the settings rather than requiring the checkbox to be ticked first.
	enableConditionMonitoringAlert: true,
	alertThresholdMode: THRESHOLD_MODE.PERCENTAGE,
	alertAboveThreshold: null,
	alertBelowThreshold: null,
	alertMessage: "",
};

const defaultErrors = {
	alertAboveThreshold: null,
	alertBelowThreshold: null,
	alertMessage: null,
};

const CMAlertConfigDialog = ({
	open,
	closeHandler,
	modelVersionTaskQuestionId,
	getError,
	onSaved,
}) => {
	const [input, setInput] = useState(defaultState);
	const [errors, setErrors] = useState(defaultErrors);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const setState = (value) => setInput((th) => ({ ...th, ...value }));

	const closeOverride = () => {
		setErrors(defaultErrors);
		setInput(defaultState);
		closeHandler();
	};

	useEffect(() => {
		// Guard against a stale response overwriting state if the question changes
		// (or the dialog closes) while a fetch is still in flight.
		let cancelled = false;

		const fetchConfig = async () => {
			if (!open || !modelVersionTaskQuestionId) return;
			setIsLoading(true);
			try {
				const response = await getConditionMonitoringAlert(
					modelVersionTaskQuestionId
				);
				if (cancelled) return;
				if (response.status) {
					const d = response.data || {};
					setInput({
						// Assume the alert is enabled when opening so the settings show
						// immediately; the user can still untick to disable.
						enableConditionMonitoringAlert: true,
						alertThresholdMode: d.alertThresholdMode ?? THRESHOLD_MODE.PERCENTAGE,
						alertAboveThreshold: d.alertAboveThreshold ?? null,
						alertBelowThreshold: d.alertBelowThreshold ?? null,
						alertMessage: d.alertMessage ?? "",
					});
				} else if (getError) {
					getError(response.data?.detail || "Could not load alert configuration");
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		fetchConfig();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, modelVersionTaskQuestionId]);

	const handleSave = async () => {
		const d = { ...input };
		if (!d.enableConditionMonitoringAlert) {
			d.alertThresholdMode = THRESHOLD_MODE.PERCENTAGE;
			d.alertAboveThreshold = null;
			d.alertBelowThreshold = null;
			d.alertMessage = "";
		} else {
			d.alertAboveThreshold =
				d.alertAboveThreshold === "" ? null : d.alertAboveThreshold;
			d.alertBelowThreshold =
				d.alertBelowThreshold === "" ? null : d.alertBelowThreshold;
		}

		const localChecker = await handleValidateObj(
			schema(d.enableConditionMonitoringAlert),
			d
		);
		if (localChecker.some((el) => el.valid === false)) {
			setErrors({ ...defaultErrors, ...generateErrorState(localChecker) });
			return;
		}

		if (
			d.enableConditionMonitoringAlert &&
			d.alertAboveThreshold == null &&
			d.alertBelowThreshold == null
		) {
			setErrors({
				...defaultErrors,
				alertAboveThreshold: "Set an Above or Below threshold",
			});
			return;
		}

		setErrors(defaultErrors);
		setIsSaving(true);
		try {
			const response = await saveConditionMonitoringAlert({
				modelVersionTaskQuestionID: parseInt(modelVersionTaskQuestionId),
				enableConditionMonitoringAlert: d.enableConditionMonitoringAlert,
				alertThresholdMode: d.alertThresholdMode,
				alertAboveThreshold:
					d.alertAboveThreshold === null ? null : parseFloat(d.alertAboveThreshold),
				alertBelowThreshold:
					d.alertBelowThreshold === null ? null : parseFloat(d.alertBelowThreshold),
				alertMessage: d.alertMessage,
			});

			if (response.status) {
				if (onSaved) onSaved();
				closeOverride();
			} else if (getError) {
				getError(response.data?.detail || "Could not save alert configuration");
			}
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Dialog
			fullWidth={true}
			maxWidth={false}
			PaperProps={{ sx: { width: "100%", maxWidth: 680 } }}
			open={open}
			onClose={closeOverride}
			aria-labelledby="cm-alert-config-dialog-title"
		>
			{isSaving && <LinearProgress />}

			<AED.ActionContainer>
				<DialogTitle id="cm-alert-config-dialog-title">
					<AED.HeaderText>Rate of Change Alert</AED.HeaderText>
				</DialogTitle>
				<AED.ButtonContainer style={{ paddingRight: "24px" }}>
					<AED.CancelButton onClick={closeOverride} variant="contained">
						Cancel
					</AED.CancelButton>
					<AED.ConfirmButton
						variant="contained"
						onClick={handleSave}
						disabled={isLoading || isSaving}
					>
						Save
					</AED.ConfirmButton>
				</AED.ButtonContainer>
			</AED.ActionContainer>

			<AED.DialogContent>
				{isLoading ? (
					<div style={{ display: "flex", alignItems: "center", padding: 16 }}>
						<CircularProgress size={20} style={{ marginRight: 8 }} />
						<Typography variant="body2">Loading...</Typography>
					</div>
				) : (
					<>
						<FormGroup row>
							<FormControlLabel
								style={{ marginLeft: 0 }}
								control={
									<EMICheckbox
										state={input.enableConditionMonitoringAlert}
										changeHandler={() =>
											setState({
												enableConditionMonitoringAlert:
													!input.enableConditionMonitoringAlert,
											})
										}
									/>
								}
								label={<Typography>Alert</Typography>}
							/>
						</FormGroup>

						{input.enableConditionMonitoringAlert ? (
							<>
								<AED.InputContainer>
									<div style={{ width: "100%" }}>
										<AED.NameLabel>Threshold Mode</AED.NameLabel>
										<ToggleButtonGroup
											exclusive
											color="primary"
											value={input.alertThresholdMode}
											onChange={(e, val) => {
												if (val !== null) {
													setState({ alertThresholdMode: val });
												}
											}}
										>
											<ToggleButton value={THRESHOLD_MODE.PERCENTAGE}>
												Percentage (%)
											</ToggleButton>
											<ToggleButton value={THRESHOLD_MODE.ABSOLUTE}>
												Absolute
											</ToggleButton>
										</ToggleButtonGroup>
									</div>
								</AED.InputContainer>

								<AED.InputContainer>
									<div style={{ width: "50%", paddingRight: 8 }}>
										<AED.NameLabel>Above Threshold</AED.NameLabel>
										<AED.NameInput
											type="number"
											error={errors.alertAboveThreshold !== null}
											helperText={errors.alertAboveThreshold}
											variant="outlined"
											value={input.alertAboveThreshold ?? ""}
											InputProps={
												input.alertThresholdMode === THRESHOLD_MODE.PERCENTAGE
													? {
															endAdornment: (
																<InputAdornment position="end">%</InputAdornment>
															),
													  }
													: undefined
											}
											onChange={(e) =>
												setState({
													alertAboveThreshold:
														e.target.value === "" ? null : e.target.value,
												})
											}
											inputProps={{ step: "any" }}
										/>
									</div>
									<div style={{ width: "50%", paddingLeft: 8 }}>
										<AED.NameLabel>Below Threshold</AED.NameLabel>
										<AED.NameInput
											type="number"
											error={errors.alertBelowThreshold !== null}
											helperText={errors.alertBelowThreshold}
											variant="outlined"
											value={input.alertBelowThreshold ?? ""}
											InputProps={
												input.alertThresholdMode === THRESHOLD_MODE.PERCENTAGE
													? {
															endAdornment: (
																<InputAdornment position="end">%</InputAdornment>
															),
													  }
													: undefined
											}
											onChange={(e) =>
												setState({
													alertBelowThreshold:
														e.target.value === "" ? null : e.target.value,
												})
											}
											inputProps={{ step: "any" }}
										/>
									</div>
								</AED.InputContainer>

								<AED.InputContainer>
									<div style={{ width: "100%" }}>
										<AED.NameLabel>
											Alert Message<AED.RequiredStar>*</AED.RequiredStar>
										</AED.NameLabel>
										<AED.NameInput
											error={errors.alertMessage !== null}
											helperText={errors.alertMessage}
											variant="outlined"
											multiline
											minRows={2}
											value={input.alertMessage ?? ""}
											onChange={(e) =>
												setState({ alertMessage: e.target.value })
											}
											inputProps={{ maxLength: 1000 }}
										/>
									</div>
								</AED.InputContainer>
							</>
						) : null}
					</>
				)}
			</AED.DialogContent>
		</Dialog>
	);
};

export default CMAlertConfigDialog;
